import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { faqPageJson } from "@/lib/faq-schema";
import { ORG_REF, PHYSICIAN_REF, PRACTICE_ADDRESS, imageRights } from "@/lib/medical-schema";
import pouchRevisionWebp800 from "@/assets/revision-hub-annotated-800.webp.asset.json";
import pouchRevisionWebp1200 from "@/assets/revision-hub-annotated-1200.webp.asset.json";
import pouchRevisionWebp1600 from "@/assets/revision-hub-annotated-1600.webp.asset.json";
import pouchRevisionJpg800 from "@/assets/revision-hub-annotated-800.jpg.asset.json";
import pouchRevisionJpg1200 from "@/assets/revision-hub-annotated-1200.jpg.asset.json";
import pouchRevisionJpg1600 from "@/assets/revision-hub-annotated-1600.jpg.asset.json";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { InlineAppointmentCTA } from "@/components/site/InlineAppointmentCTA";
import { RevisionHashRedirect } from "@/components/site/RevisionHashRedirect";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { schemaDateFields } from "@/lib/schema-dates";

const revisionsOgImage = pouchRevisionJpg1200;
const REVISIONS_IMAGE_ALT =
  "Laparoscopic view before gastric bypass revision showing a dilated pouch marked with a blue arrow and a large hiatal hernia marked with a blue circle";
const REVISIONS_IMAGE_CAPTION =
  "Dr. Fitzer prepares to perform a revision for a patient with unacceptable weight loss and severe heartburn. They have a dilated pouch (blue arrow) and a large hiatal hernia that needs to be repaired (blue circle).";

const REVISIONS_IMAGE_RIGHTS = imageRights("https://virginiabariatric.com/services/revisions");

export const Route = createFileRoute("/services/revisions/")({
  head: () => ({
    meta: [
      { title: "Revision Bariatric Surgery in Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Weight regain or complications after bariatric surgery? Dr. Matthew Fitzer performs sleeve, gastric bypass, and LAP-BAND revisions across Virginia.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Revision Bariatric Surgery in Virginia | Virginia Bariatric" },
        { property: "og:description", content: "Weight regain or complications after bariatric surgery? Dr. Matthew Fitzer performs sleeve, gastric bypass, and LAP-BAND revisions across Virginia." },
        { property: "og:url", content: "https://virginiabariatric.com/services/revisions" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: `https://virginiabariatric.com${revisionsOgImage.url}` },
        { property: "og:image:alt", content: REVISIONS_IMAGE_ALT },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "668" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Revision Bariatric Surgery in Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "Weight regain or complications after bariatric surgery? Dr. Matthew Fitzer performs sleeve, gastric bypass, and LAP-BAND revisions across Virginia." },
        { name: "twitter:image", content: `https://virginiabariatric.com${revisionsOgImage.url}` },
        { name: "twitter:image:alt", content: REVISIONS_IMAGE_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/services/revisions") : []),
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "@id": "https://virginiabariatric.com/services/revisions#webpage",
          ...workTranslationOf("/services/revisions"),
          reviewedBy: PHYSICIAN_REF,
          name: "Revision Bariatric Surgery: Second-Chance Weight Loss",
          url: "https://virginiabariatric.com/services/revisions",
          mainEntity: { "@id": "https://virginiabariatric.com/services/revisions#procedure" },
          description:
            "Gastric sleeve revision, gastric bypass revision options, LAP-BAND removal or conversion, and how to get insurance to cover revision bariatric surgery in Northern Virginia with Dr. Matthew Fitzer.",
          significantLink: [
            "https://virginiabariatric.com/services/revisions/sleeve",
            "https://virginiabariatric.com/services/revisions/gastric-bypass",
            "https://virginiabariatric.com/services/revisions/lap-band",
            "https://virginiabariatric.com/services/revisions/tore",
            "https://virginiabariatric.com/services/revisions#insurance",
          ],

          hasPart: [
            {
              "@type": "WebPageElement",
              name: "Insurance coverage for revision surgery",
              url: "https://virginiabariatric.com/services/revisions#insurance",
              cssSelector: "#insurance",
            },
          ],

          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Virginia Bariatric Surgery",
            url: "https://virginiabariatric.com",
          },
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: `https://virginiabariatric.com${revisionsOgImage.url}`,
            caption: REVISIONS_IMAGE_CAPTION,
            width: 1200,
            height: 668,
            contentUrl: `https://virginiabariatric.com${revisionsOgImage.url}`,
            inLanguage: "en-US",
            ...REVISIONS_IMAGE_RIGHTS,
          },
          about: {
            "@type": "MedicalCondition",
            name: "Weight regain and insufficient weight loss after bariatric surgery",
            code: {
              "@type": "MedicalCode",
              codingSystem: "ICD-10",
              codeValue: "E66.9",
            },
          },
          specialty: {
            "@type": "MedicalSpecialty",
            name: "Bariatric Surgery",
          },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patient",
            geographicArea: {
              "@type": "AdministrativeArea",
              name: "Northern Virginia",
            },
          },
          ...schemaDateFields("/services/revisions"),
          mainContentOfPage: {
            "@type": "WebPageElement",
            cssSelector: "article",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": "https://virginiabariatric.com/services/revisions#procedure",
          mainEntityOfPage: { "@id": "https://virginiabariatric.com/services/revisions#webpage" },
          procedureType: "https://schema.org/SurgicalProcedure",
          name: "Revision Bariatric Surgery",
          alternateName: [
            "Gastric sleeve revision surgery",
            "Sleeve gastrectomy revision",
            "Gastric bypass revision surgery",
            "LAP-BAND revision and removal",
            "Redo Bariatric Surgery",
          ],
          description:
            "Revision bariatric surgery corrects, modifies, or converts a previous weight-loss operation. Options include gastric sleeve revision (sleeve-to-bypass conversion, re-sleeve, or SADI/duodenal switch), gastric bypass revision such as transoral outlet reduction (TORe) and distalization, and LAP-BAND removal or conversion.",
          url: "https://virginiabariatric.com/services/revisions",

          image: {
            "@type": "ImageObject",
            url: `https://virginiabariatric.com${revisionsOgImage.url}`,
            caption: REVISIONS_IMAGE_CAPTION,
            width: 1200,
            height: 668,
            contentUrl: `https://virginiabariatric.com${revisionsOgImage.url}`,
            inLanguage: "en-US",
            ...REVISIONS_IMAGE_RIGHTS,
          },
          bodyLocation: "Stomach and small intestine",
          howPerformed:
            "Performed laparoscopically (minimally invasive) or endoscopically through the mouth, depending on the type of revision. Most surgical revisions require a single-night hospital stay.",
          preparation:
            "Review of prior operative records, upper endoscopy or contrast imaging to assess current anatomy, nutritional labs, and dietitian and medical evaluations.",
          followup:
            "Staged post-operative diet, lifelong vitamin and mineral supplementation, nutritional coaching, activity progression, and long-term medical follow-up.",
          status: "https://schema.org/ActiveActionStatus",
          medicineSystem: "https://schema.org/WesternConventional",
          relevantSpecialty: {
            "@type": "MedicalSpecialty",
            name: "Bariatric Surgery",
          },
          recognizingAuthority: ORG_REF,
        }),
      },
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Why do patients regain weight after bariatric surgery?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Weight regain is usually about biology, not behavior. Hunger hormones rise and metabolism slows after weight loss. The anatomy can change too — sleeves stretch, pouches widen, and bands slip or erode. A revision workup finds out which of these is happening and what will fix it.",
              },
            },
            {
              "@type": "Question",
              name: "Is revision bariatric surgery safe?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Revisions carry somewhat more risk than a first operation, because they work through scar tissue. Done through small incisions at an accredited center, by a surgeon who does them often, the risk stays low. Most patients go home after one night.",
              },
            },
            {
              "@type": "Question",
              name: "Does insurance cover revision bariatric surgery?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most plans cover revisions that are medically needed. Approval is easier when there is a clear problem, such as a slipped band or reflux after a sleeve. For weight regain alone, plans usually ask you to meet their current BMI and health rules again. Our office handles the paperwork with you.",
              },
            },
            {
              "@type": "Question",
              name: "How do I find out which revision I need?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Start with a consultation. We review your operative report, check your labs, and look at your current anatomy with endoscopy or imaging. The right option follows from what we find and what you want to achieve.",
              },
            },
          ],

        }),
      },
    ] : [],

  }),
  component: RevisionsPage,
});

function RevisionsPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <RevisionHashRedirect />
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">Revision Bariatric Surgery: Second-Chance Weight Loss</h1>
        <p className="mt-1 text-foreground/70 italic">A second chance at the results you set out to achieve</p>
        <nav aria-label="Jump to a revision type" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link to="/services/revisions/sleeve" preload="intent" className="text-brand-deep underline hover:no-underline">Gastric sleeve revision</Link>
          <Link to="/services/revisions/gastric-bypass" preload="intent" className="text-brand-deep underline hover:no-underline">Gastric bypass revision</Link>
          <Link to="/services/revisions/lap-band" preload="intent" className="text-brand-deep underline hover:no-underline">LAP-BAND revision</Link>
          <Link to="/services/revisions/tore" preload="intent" className="text-brand-deep underline hover:no-underline">TORe</Link>
          <a href="#insurance" className="text-brand-deep underline hover:no-underline">Insurance coverage</a>
        </nav>
      </header>

      <MedicalReviewBanner lang="en" className="mt-4" />

      <p className="mt-3 text-foreground/80">
        Is your bariatric surgery no longer working the way it once did? You are not alone, and you have options. Revision bariatric surgery (also called redo surgery or conversion surgery) often can restore the effectiveness of a malfunctioning bariatric operation. Revisions are any procedures that fix, change, or convert an earlier weight-loss operation so that it works again. Dr. Matthew Fitzer has decades of experience performing every major type of revision.
      </p>

      <TelehealthCta />

      <p className="mt-3 text-foreground/80">
        These procedures are done in one of two ways. Many are done through small incisions. Others are done through the mouth, with no incisions at all. They are for patients who have experienced less-than-expected weight loss, regained their weight, or developed problems such as reflux or painful swallowing after a sleeve, bypass, or a gastric band. Reflux after a sleeve is often tied to a hiatal hernia, which can be repaired at the same operation — see <Link to="/services/hiatal-hernia-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">hiatal hernia repair</Link>.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Why weight returns — and why it is not your fault</h2>
      <p className="mt-3 text-foreground/80">
        Weight regain after bariatric surgery is determined by biology, not by willpower. When you lose weight, your body pushes back. Hunger hormones rise. Fullness signals fade. The calories you burn at rest can quietly fall.
      </p>
      <p className="mt-3 text-foreground/80">
        The anatomy of your operation can change over time, too. A sleeve can stretch. A bypass pouch or its outlet (the stoma) can widen. A band can slip, erode, or simply stop helping. When the tool stops working, the answer is not to keep going with a broken tool. The answer is to fix it. Enter revision surgery. For a fuller walkthrough, see our guide to <Link to="/resources/managing-weight-regain-after-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">managing weight regain after bariatric surgery</Link>. Regained weight can bring health problems back with it — especially <Link to="/resources/obesity-and-diabetes" preload="intent" className="text-brand-mid underline">type 2 diabetes</Link>, where the evidence for surgery is unusually strong.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Which revision is right for you?</h2>
      <p className="mt-3 text-foreground/80">
        The right revision depends on the operation you had first. Start with the page that matches your original surgery.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/services/revisions/sleeve" preload="intent" className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl">
          <h3 className="font-serif text-[1.125rem] text-brand-deep">Gastric sleeve revision</h3>
          <p className="mt-1 text-sm text-foreground/80">For weight regain, poor weight loss, swallowing problems, or reflux after a sleeve. Options include sleeve-to-bypass conversion, re-sleeve, and SADI/duodenal switch.</p>
        </Link>
        <Link to="/services/revisions/gastric-bypass" preload="intent" className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl">
          <h3 className="font-serif text-[1.125rem] text-brand-deep">Gastric bypass revision</h3>
          <p className="mt-1 text-sm text-foreground/80">For weight regain after a gastric bypass. Options include outlet repair through the mouth (TORe), laparoscopic pouch revision, and distalization.</p>
        </Link>
        <Link to="/services/revisions/lap-band" preload="intent" className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl">
          <h3 className="font-serif text-[1.125rem] text-brand-deep">LAP-BAND removal and conversion</h3>
          <p className="mt-1 text-sm text-foreground/80">For slipped, eroded, or failed gastric bands. The band can come out alone or be converted to a sleeve or bypass. Dr. Fitzer prefers to do both in one operation.</p>
        </Link>
        <Link to="/services/revisions/tore" preload="intent" className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl">
          <h3 className="font-serif text-[1.125rem] text-brand-deep">TORe</h3>
          <p className="mt-1 text-sm text-foreground/80">Transoral outlet reduction after gastric bypass.</p>
        </Link>
      </div>

      <figure className="mt-6" aria-label="Laparoscopic photo before gastric bypass pouch revision">
        <picture>
          <source
            type="image/webp"
            srcSet={`${pouchRevisionWebp800.url} 800w, ${pouchRevisionWebp1200.url} 1200w, ${pouchRevisionWebp1600.url} 1600w`}
            sizes="(min-width: 1024px) 800px, 100vw"
          />
          <img
            src={pouchRevisionJpg800.url}
            srcSet={`${pouchRevisionJpg800.url} 800w, ${pouchRevisionJpg1200.url} 1200w, ${pouchRevisionJpg1600.url} 1600w`}
            sizes="(min-width: 1024px) 800px, 100vw"
            width={1600}
            height={890}
            alt={REVISIONS_IMAGE_ALT}
            aria-describedby="revision-caption"
            className="w-full rounded-lg bg-muted bg-cover bg-center"
            style={{
              backgroundImage:
                "url(data:image/webp;base64,UklGRn4AAABXRUJQVlA4IHIAAACwBACdASoUAAsAPu1iqU2ppaOiMAgBMB2JbACdMoRwACxjkh7YAJR/fGTl5UkAAP7sx0s7eA/mu6p2WLiu5S0HzZs9/Gl0obBXdr+EgiGfxFR1hasG5N03wO8q+Q/MC7N2s9Xkj53Jl9rLnHuqIC1gAAA=)",
            }}
            loading="lazy"
            fetchPriority="low"
            decoding="async"
          />
        </picture>
        <figcaption id="revision-caption" className="mt-2 text-center text-sm text-foreground/70">
          Dr. Fitzer prepares to perform a revision for a patient with unacceptable weight loss and severe heartburn. They have a dilated pouch (blue arrow) and a large hiatal hernia that needs to be repaired (blue circle).
        </figcaption>
      </figure>

      <InlineAppointmentCTA
        heading="Not sure which revision you need?"
        body="Start with a consultation. Dr. Fitzer will review your prior operation, current anatomy, and goals, then recommend the safest path forward."
        buttonText="Schedule an Appointment to discuss revision bariatric surgery"
        secondaryButton={{ text: "Check Revision Surgery Insurance Coverage", to: "/resources/insurance-coverage" }}
      />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">The revision evaluation process</h2>
      <p className="mt-3 text-foreground/80">
        Every revision starts with a conversation. Dr. Fitzer will ask you about your experience with the prior operation. Did it ever work? He will also ask about your goals and how the current surgery is not meeting them.
      </p>
      <p className="mt-3 text-foreground/80">
        Then he will explore the question: what does the relevant anatomy look like today? He reviews your records and the original operative report. Then he examines the status of your GI tract directly. An imaging test called an upper GI series usually is enough to provide the needed information. Sometimes a procedure called upper endoscopy (EGD) is done to answer a specific question.
      </p>
      <p className="mt-3 text-foreground/80">
        He will order lab tests to check your vitamin and mineral levels. Our dietitian reviews your eating patterns and offers relevant advice, since the right habits help patients get the best operative results.
      </p>
      <p className="mt-3 text-foreground/80">
        Finally, Dr. Fitzer walks you through your options. He helps you understand the best fit given your anatomy and your goals. The options may include weight-loss medication, surgery, or both.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Is revision surgery safe?</h2>
      <p className="mt-3 text-foreground/80">
        Revision operations work on changed anatomy, often through scar tissue. The large national databases show they carry somewhat more risk than a first surgery, and they take 30 to 60 minutes longer. In experienced hands at an accredited center, the risk stays low. A review of 39 studies found that conversion surgery after a failed restrictive operation offers a strong balance of results and safety.<sup className="text-[0.7em]">1</sup> Revision surgery is a special focus of Dr. Fitzer&rsquo;s practice, with over twenty-one years of experience performing them. Careful patient selection, small-incision technique, and close follow-up keep risks as low as possible.
      </p>

      <h2 id="insurance" className="mt-6 scroll-mt-24 text-[1.35rem] md:text-[1.6875rem] font-serif">Insurance coverage for revision surgery</h2>
      <p className="mt-3 text-foreground/80">
        Most <Link to="/resources/insurance-coverage" preload="intent" className="text-brand-deep underline hover:no-underline">insurance</Link> plans cover revision surgery, though they sometimes ask for more paperwork than they did the first time. Approval is most certain when the problem is one of the common, accepted triggers for revision: a slipped band, a stricture, or reflux after a sleeve.
      </p>
      <p className="mt-3 text-foreground/80">
        If weight regain is the only reason, the regain has to be substantial. Most plans require you to meet the BMI and health criteria that first-time bariatric surgery patients must meet, which our <Link to="/resources/qualifications" preload="intent" className="text-brand-deep underline hover:no-underline">bariatric surgery requirements</Link> page explains.
      </p>
      <p className="mt-3 text-foreground/80">
        We will gather the records: your operative reports, office notes, imaging or endoscopy, and your weight history. We handle the prior authorization with you. If a denial is not clearly supported by the plan&rsquo;s own rules, we file an appeal on your behalf. If you are not using insurance, our self-pay bariatric surgery page lists{" "}
        <Link to="/self-pay-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">
          our published prices for revision procedures.
        </Link>

      </p>
      <p className="mt-3 text-foreground/80">
        Not using insurance? See the{" "}
        <Link to="/self-pay-bariatric-surgery" hash="revision-surgery-cost" preload="intent" className="text-brand-deep underline hover:no-underline">
          revision surgery cost
        </Link>{" "}
        self-pay: an estimated $25,400, which covers the surgeon fee, one hospital day at INOVA Fair
        Oaks Hospital, an anesthesia estimate, and a year of routine follow-up with Dr. Fitzer. See
        our{" "}
        <Link to="/resources/prosper" preload="intent" className="text-brand-deep underline hover:no-underline">
          financing options
        </Link>{" "}
        for ways to spread payments over time.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Revision bariatric surgery for patients across Virginia</h2>
      <p className="mt-3 text-foreground/80">
        Revisions draw patients from well beyond Northern Virginia, including Richmond, Charlottesville, Fredericksburg and Hampton Roads. Many start with a telehealth consultation. Endoscopic revisions like TORe are outpatient, and surgical revisions usually need one night at INOVA Fair Oaks Hospital in Fairfax. We plan visits so travelers make as few trips to Herndon as possible.
      </p>
      <p className="mt-3 text-foreground/80">
        Because there are relatively few experts in revision surgery, patients often travel to us from much farther away. We treat a large number of patients from Richmond, Charlottesville, Fredericksburg, Newport News, Norfolk, Virginia Beach, and Suffolk. We designed our process to make it convenient for out-of-town patients: telehealth initial consults and office visits, combined pre-op appointments, and follow-up by telehealth or planned around your travel.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Frequently asked questions</h2>
      <dl className="defer-render mt-2 space-y-3 text-foreground/80">
        <div>
          <dt className="font-medium text-foreground">Why do patients regain weight after bariatric surgery?</dt>
          <dd className="mt-1">Weight regain is usually about biology, not behavior. Hunger hormones rise and metabolism slows after weight loss. The anatomy can change too — sleeves stretch, pouches widen, and bands slip or erode. A revision workup finds out which of these is happening and what will fix it.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">Is revision bariatric surgery safe?</dt>
          <dd className="mt-1">Revisions carry somewhat more risk than a first operation, because they work through scar tissue. Done through small incisions at an accredited center, by a surgeon who does them often, the risk stays low. Most patients go home after one night.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">Does insurance cover revision bariatric surgery?</dt>
          <dd className="mt-1">Most plans cover revisions that are medically needed. Approval is easier when there is a clear problem, such as a slipped band or reflux after a sleeve. For weight regain alone, plans usually ask you to meet their current BMI and health rules again. Our office handles the paperwork with you.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">How do I find out which revision I need?</dt>
          <dd className="mt-1">Start with a consultation. We review your operative report, check your labs, and look at your current anatomy with endoscopy or imaging. The right option follows from what we find and what you want to achieve.</dd>
        </div>
      </dl>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">You deserve a second chance</h2>
      <p className="mt-3 text-foreground/80">
        A bariatric operation that has stopped working is a problem we can solve. Needing help again is nothing to be ashamed of. Ready to talk about your options? Request a consultation online or call <a href="tel:+17037099771" className="text-brand-deep underline hover:no-underline">(703) 709-9771</a>.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>


      <div className="mt-4 text-sm text-foreground/70">
        <h2 className="font-medium">Selected medical references</h2>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>
            Chierici A, et al. Postoperative morbidity and weight loss after revisional bariatric surgery for primary failed restrictive procedure: a systematic review and network meta-analysis. <em>Int J Surg</em>. 2022;102:106677. doi:10.1016/j.ijsu.2022.106677
          </li>
        </ol>
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}

